<script>
import AdminListActions from "@/components/Admin/ListActions.vue";
import MailingListForm from "@/components/Admin/MailingListForm.vue";
import IconButton from "@/components/shared/IconButton.vue";
import PaginatedTable from "@/components/shared/PaginatedTable.vue";
import { Column } from "@/components/shared/PaginatedTableColumns";
import { capitalize } from "@/helpers/filters";
import { del } from "@/requests/server";

export default {
  name: "AdminMailingLists",
  components: { AdminListActions, MailingListForm, IconButton, PaginatedTable },
  data() {
    return {
      columns: [
        new Column("id", capitalize(this.$t("views.admin.mailing_lists.fields.id")), "id"),
        new Column(
          "provider",
          capitalize(this.$t("views.admin.mailing_lists.fields.provider")),
          "text",
          {
            formatter: capitalize,
          }
        ),
        new Column(
          "list_name",
          capitalize(this.$t("views.admin.mailing_lists.fields.list_name")),
          "text"
        ),
        Column.withRelation(
          "created_by_user.full_name",
          capitalize(this.$t("views.admin.mailing_lists.fields.created_by")),
          {
            relation: "users",
            label: "full_name",
            field: "id",
            params: { for: "admin" },
          },
          "created_by_user_id",
          {
            urlFct: (mailingList) => `/admin/users/${mailingList.created_by_user.id}`,
          }
        ),
        Column.withRelation(
          "community.name",
          capitalize(this.$t("communities.fields.community.name")),
          {
            relation: "communities",
            label: "name",
            field: "id",
            params: { for: "admin" },
          },
          "community_id",
          {
            urlFct: (mailingList) => `/admin/communities/${mailingList.community.id}`,
          }
        ),
        Column.withoutFilter(
          "api_key",
          capitalize(this.$t("views.admin.mailing_lists.fields.api_key")),
          "text",
          { sortable: false }
        ),
        Column.withSelect(
          "status",
          capitalize(this.$t("views.admin.mailing_lists.fields.status.label")),
          [
            {
              value: null,
              label: capitalize(this.$t("views.admin.mailing_lists.fields.status.options.all")),
            },
            {
              value: "active",
              label: capitalize(this.$t("views.admin.mailing_lists.fields.status.options.active")),
              variant: "success",
            },
            {
              value: "synchronizing",
              label: capitalize(
                this.$t("views.admin.mailing_lists.fields.status.options.synchronizing")
              ),
              variant: "warning",
            },
            {
              value: "suspended",
              label: capitalize(
                this.$t("views.admin.mailing_lists.fields.status.options.suspended")
              ),
              variant: "danger",
            },
          ]
        ),
        Column.withoutFilter(
          "last_error",
          capitalize(this.$t("views.admin.mailing_lists.fields.last_error")),
          "text"
        ),
        new Column(
          "community_editable",
          capitalize(this.$t("views.admin.mailing_lists.fields.community_editable")),
          "boolean",
          {
            showByDefault: false,
          }
        ),
      ],
    };
  },
  computed: {},
  methods: {
    async deleteIntegration(item) {
      await del(`/mailinglists/${item.id}`, {
        notifications: {
          action: this.$t("views.admin.mailing_lists.actions.delete.notification.action"),
          onSuccess: this.$t("views.admin.mailing_lists.actions.delete.notification.success"),
        },
      });
      this.$refs.table.refresh();
    },
    copyErrorMessage(errorMessage) {
      navigator.clipboard.writeText(errorMessage).then(() => {
        this.$store.commit("addNotification", {
          title: this.$t("views.admin.mailing_lists.error.copied"),
          variant: "success",
        });
      });
    },
  },
};
</script>

<template>
  <div>
    <h2>{{ $t("views.admin.mailing_lists.title") }}</h2>
    <paginated-table
      ref="table"
      endpoint="mailinglists"
      :columns="columns"
      :show-generate-csv="false"
      row-link
    >
      <template #head-buttons>
        <icon-button v-b-modal="'add-mailing-list-modal'" role="add">{{
          $t("views.admin.mailing_lists.buttons.add")
        }}</icon-button>
      </template>
      <template #cell(actions)="{ item }">
        <admin-list-actions
          slug="mailinglists"
          :archive-label="$t('views.admin.mailing_lists.actions.delete.label')"
          :destroy-action="() => deleteIntegration(item)"
          :item-name="$t('views.admin.mailing_lists.actions.delete.item_name')"
          :item="item"
          :show-restore="false"
        />
      </template>
      <template #cell(last_error)="{ item }">
        <b-modal
          v-if="item.last_error"
          :id="`mailing-list-error-${item.id}`"
          hide-footer
          hide-header
          size="lg"
          centered
        >
          <h4>{{ $t("views.admin.mailing_lists.error.title") }}</h4>
          <pre class="text-monospace">{{ item.last_error }}</pre>

          <icon-button icon="files" class="mt-2" @click="() => copyErrorMessage(item.last_error)">
            {{ $t("views.admin.mailing_lists.buttons.copy") }}
          </icon-button>
        </b-modal>
        <icon-button
          v-if="item.last_error"
          v-b-modal="`mailing-list-error-${item.id}`"
          variant="ghost-secondary"
          size="sm"
        >
          {{ $t("views.admin.mailing_lists.buttons.show") }}
        </icon-button>
      </template>
    </paginated-table>

    <b-modal id="add-mailing-list-modal" hide-header hide-footer>
      <mailing-list-form
        community-editable
        @added="
          () => {
            $refs.table.refresh();
            $bvModal.hide('add-mailing-list-modal');
          }
        "
      />
    </b-modal>
  </div>
</template>

<style scoped lang="scss">
.card {
  box-shadow: $small-shadow;
  + .card {
    margin-top: 1rem;
  }
}
</style>
